import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import { all, call, put, putResolve, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import { client } from '@portal/api-client/src/client';
import type { Whitelist } from '@portal/api-models';
import { getPortalError } from '@portal/common';

import * as actions from './actions';

export function* getAllWhitelist() {
  try {
    const { data: response }: AxiosResponse<Whitelist[]> = yield call(
      [client, 'get'],
      '/whitelist/',
      {
        params: { all: true, limit: 1000 },
      }
    );

    yield put(actions.getAllWhitelistAction.success(response));
  } catch (err: any) {
    yield put(actions.getAllWhitelistAction.failure(err));
  }
}

export function* createWhitelist(
  action: ActionType<typeof actions.createWhitelistAction.request>
) {
  try {
    const { data: response }: AxiosResponse<Whitelist> = yield call(
      [client, 'post'],
      '/whitelist/',
      action.payload.row
    );

    yield call([message, 'success'], 'Whitelist created successfully');
    yield put(actions.createWhitelistAction.success(response));
    yield put(actions.toggleCreateWhitelist());
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.createWhitelistAction.failure(portalError));
  }
}

export function* createMultipleWhitelist(
  action: ActionType<typeof actions.createMultipleWhitelistAction.request>
) {
  try {
    const response: Whitelist[] = yield all(
      action.payload.rows.map(function* (payload) {
        const { data }: AxiosResponse<Whitelist> = yield call(
          [client, 'post'],
          '/whitelist/',
          payload
        );

        return data;
      })
    );

    yield call([message, 'success'], 'Whitelist created successfully');
    yield putResolve(actions.createMultipleWhitelistAction.success(response));
    yield putResolve(actions.toggleCreateWhitelist());
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.createMultipleWhitelistAction.failure(portalError));
  }
}

export function* deleteWhitelist(
  action: ActionType<typeof actions.deleteWhitelistAction.request>
) {
  try {
    yield call([client, 'delete'], `/whitelist/${action.payload.uuid}`);
    yield call([message, 'success'], 'Whitelist deleted successfully');

    yield put(
      actions.deleteWhitelistAction.success({ uuid: action.payload.uuid })
    );
  } catch (err: any) {
    yield put(actions.deleteWhitelistAction.failure(err));
  }
}

export function* whitelistSaga() {
  yield takeLatest(
    getType(actions.getAllWhitelistAction.request),
    getAllWhitelist
  );
  yield takeLatest(
    getType(actions.createWhitelistAction.request),
    createWhitelist
  );
  yield takeLatest(
    getType(actions.createMultipleWhitelistAction.request),
    createMultipleWhitelist
  );
  yield takeLatest(
    getType(actions.deleteWhitelistAction.request),
    deleteWhitelist
  );
}
